/*
 * WHICH KPI VOCABULARY A CAMPAIGN-SCOPE STRIP SPEAKS — one ruling, both
 * portals.
 *
 * THE PROBLEM THIS EXISTS TO SOLVE
 *
 * Platform Analytics' ALL PLATFORMS strip is a MIXED-FLIGHT vocabulary. Spend,
 * Impressions, Clicks, CTR, CPQV, Qualified Video Views, the outcome and Data
 * Freshness are the eight readings that mean something whatever combination of
 * CTV, YouTube, Display and social a campaign happens to be running, and for a
 * campaign running several of them that is exactly the right list.
 *
 * A campaign delivering on ONE platform is not that campaign. Its aggregate is
 * that platform's row and nothing else, so the mixed-flight compromise buys
 * nothing and costs two or three primary positions: a CTV-only flight spent
 * one position on Clicks (a zero-filled 0), one on CTR (a permanent `—`), and
 * had nowhere to say how many plays started or what share of them finished.
 * The reader was shown the generic account of a campaign that has no generic
 * about it — and the answer was already in the building, one drill-down away,
 * on the platform's own registry profile.
 *
 * WHAT THIS MODULE IS
 *
 * The predicate, and only the predicate. It answers "does this campaign's
 * reporting scope resolve to exactly one platform, and if so which" — and then
 * hands the KPI question straight to `focusedPlatformKpiProfile`, which is
 * already the one place a platform's ordered KPI list is resolved from. There
 * is deliberately no metric list here: a second list is how the ALL PLATFORMS
 * strip and the drill-down would come to state the same single-platform
 * campaign differently, which is the defect, not the fix.
 *
 * So the contract is:
 *
 *   2+ reportable platforms  → CROSS_PLATFORM   → the generic strip, unchanged
 *   exactly 1                → SINGLE_PLATFORM  → that platform's registry KPIs
 *   0                        → NO_DELIVERY      → the pre-live state, unchanged
 *
 * WHAT "REPORTABLE" MEANS, AND WHY IT IS NOT `campaign.platforms.length`
 *
 * The population handed in must be the one the surface already aggregates
 * over: in Cortex, `model.platforms` from `buildPlatformAnalyticsModel`; in
 * Quantum, the delivering keys the cross-platform dataset resolved. Both have
 * already applied the governed identity rules — measurement-only sources
 * excluded, the Meta family folded to one platform, and only rows carrying
 * positive spend/impression/click evidence surviving. That is what makes a
 * platform that is CONFIGURED but has not delivered stop forcing the generic
 * strip, and what stops a stale provider identity inventing a second platform.
 * A configured-platform count read off the campaign record would do neither.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * It does not decide anything for a platform that has declared no KPI profile
 * kind. `metricKeys` is null there, and null means "the generic strip,
 * unchanged" — so a sole TikTok or Search campaign keeps exactly the strip it
 * had. Like the module it delegates to, this one can only ever add an
 * explicit, declared exception.
 *
 * It also does not change SCOPE. A single-platform campaign stays in ALL
 * PLATFORMS: nothing here focuses a platform, persists a drill-down, or alters
 * reset. The aggregate is unchanged and still campaign-wide — it simply
 * contains one platform, and is therefore described in that platform's own
 * vocabulary.
 */

import { resolveFocusedKpiMetricKeys } from "./focusedPlatformKpiProfile";

export const CAMPAIGN_KPI_SCOPE_CROSS_PLATFORM = "cross_platform";
export const CAMPAIGN_KPI_SCOPE_SINGLE_PLATFORM = "single_platform";
export const CAMPAIGN_KPI_SCOPE_NO_DELIVERY = "no_delivery";

/*
 * Callers hold their reportable population in two shapes — Cortex as model
 * rows (`{ key, label, ... }`), Quantum as bare channel keys — and neither is
 * asked to convert before calling. A blank entry is dropped rather than
 * counted: an empty string is not a platform, and counting one would be the
 * "a second platform exists" mistake in its cheapest form.
 */
const readPlatformKey = (entry) => {
  const raw = entry && typeof entry === "object" ? entry.key ?? entry.platformKey : entry;
  const key = String(raw == null ? "" : raw).trim();
  return key || null;
};

/*
 * Distinct keys, in the order the caller supplied them. De-duplicated because
 * the count is the whole ruling: one platform reaching this list twice under
 * the same key would read as a mixed flight and silently restore the generic
 * strip — the exact failure this module is supposed to prevent.
 */
const distinctPlatformKeys = (platforms) => {
  const seen = new Set();
  const keys = [];
  (Array.isArray(platforms) ? platforms : []).forEach((entry) => {
    const key = readPlatformKey(entry);
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  });
  return keys;
};

/**
 * The KPI vocabulary this reporting scope should speak.
 *
 * Returns `{ mode, platformKey, platformCount, metricKeys }`:
 *
 * - `mode` is one of the three constants above.
 * - `platformKey` is the sole reportable platform, or null.
 * - `metricKeys` is that platform's ordered registry KPI list, or null — null
 *   whenever the generic strip applies, whether because the scope is mixed,
 *   empty, or resolves to a platform that declares no profile of its own.
 *
 * `metricKeys` is null for every mode but SINGLE_PLATFORM, so a caller may
 * branch on it alone and get the conservative answer by default.
 */
export function resolveCampaignKpiScope(platforms = []) {
  const keys = distinctPlatformKeys(platforms);

  if (keys.length === 0) {
    return {
      mode: CAMPAIGN_KPI_SCOPE_NO_DELIVERY,
      platformKey: null,
      platformCount: 0,
      metricKeys: null,
    };
  }

  if (keys.length > 1) {
    return {
      mode: CAMPAIGN_KPI_SCOPE_CROSS_PLATFORM,
      platformKey: null,
      platformCount: keys.length,
      metricKeys: null,
    };
  }

  const platformKey = keys[0];
  return {
    mode: CAMPAIGN_KPI_SCOPE_SINGLE_PLATFORM,
    platformKey,
    platformCount: 1,
    /* Resolved, never listed. This is the same call the drill-down makes, so
       the aggregate and the focused view of a one-platform campaign cannot
       name different metrics in different orders. */
    metricKeys: resolveFocusedKpiMetricKeys(platformKey),
  };
}

/**
 * Does this reporting scope resolve to one platform WITH a declared KPI
 * profile — the single question a surface has to answer before deciding
 * whether to render the generic strip.
 */
export function campaignKpiScopeIsSinglePlatform(scope) {
  return Boolean(
    scope &&
      scope.mode === CAMPAIGN_KPI_SCOPE_SINGLE_PLATFORM &&
      Array.isArray(scope.metricKeys) &&
      scope.metricKeys.length
  );
}
